import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../store/types';
import { AvailableLanguageInterface } from '../../services/exports/Interfaces';
import { getActiveLanguages } from '../../services/localization/Languages';
import LocalizationManager from '../../services/localization/LocalizationManager';

interface UseLanguage {
  language?: string;
  languages: AvailableLanguageInterface[];
  changeLanguage: (language: string) => Promise<void>;
  hasChoice: boolean;
}

/**
 * The customer language picker's data source and its one write path.
 *
 * The choice is the customer's own and lasts their visit — i18next caches it through
 * LocalizationManager, and nothing is written server-side. It never touches the
 * restaurant's configured language, so the next customer still starts in the language
 * the restaurant set. It also does not translate the restaurant's own content:
 * switching changes the platform chrome, not menu item names, which stay as the
 * restaurant entered them.
 */
export default function useLanguage(): UseLanguage {
  const { i18n } = useTranslation();

  const { company } = useSelector((state: StoreInterface) => state.initialData);

  const languages = useMemo(() => getActiveLanguages(company?.supported_languages), [company?.supported_languages]);

  // The detector can hand back a regional tag ('de-DE'); catalogs are keyed by the
  // base code, so normalise before comparing or feeding a picker's value.
  const language = i18n.language?.split(/[-_]/)[0];

  async function changeLanguage(next: string): Promise<void> {
    if (!next || next === language) {
      return;
    }

    try {
      await i18n.changeLanguage(next);
    } catch (error) {
      console.error('Failed to change language', error);

      return;
    }

    // From here on the language is the customer's own: later initial-data refreshes (window
    // focus, login, checkout) must not reset it to the restaurant's default. The detector
    // only flags choices carried over from before this page load.
    LocalizationManager.markExplicitChoice();

    // Deliberately does NOT write users.lang. The pick is session-only for v1 — the
    // ticket lists a persistent customer language preference as out of scope — and
    // persisting it would reach further than this repo: foodamigos-marketing localises
    // to "<lang>-<country>" with the customer's own language as the fallback locale
    // (AppLocalizer::setFallbackLocale), and it only ships de/en templates. A stored
    // 'pl' there resolves to neither pl-DE nor pl, and trans() returns the key — every
    // loyalty, members-club and promo mail would go out with `subjects.*` as its
    // subject. Restore this only together with those templates.
  }

  return {
    language,
    languages,
    changeLanguage,
    // Hidden with a single choice, per the ticket.
    hasChoice: languages.length > 1,
  };
}
